import React, { useState, useEffect } from "react";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ExpandableTablegrievancetable.css";
import TableHeader from "../../../Components/UiComponents/Table/TableHeader/TableHeader";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import SvgSortdownarrow from "../../../Components/IconComponents/Sortdownarrow";
import SvgSortuparrow from "../../../Components/IconComponents/Sortuparrow";
import SvgSorting from "../../../Components/IconComponents/Sortingarrow";
import { getData } from "../../../Services/Services";
import {  getThemeURL } from "../../../Services/EndPoints";
// import { toast } from "react-toastify";
import {
  Viewicon,
} from "../../../Components/IconComponents";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";

// Default filter UI for a column
const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined); // undefined to remove the filter entirely
      }}
      placeholder={`Filter...`}
      style={{
        width: "100%",
        padding: "5px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "5px",
      }}
    />
  );
};

// Status mapping function (same as in parent component)
const getStatusFromId = (statusId) => {
  const statusMap = {
    837: "Open",
    838: "In Progress",
    839: "Hold",
    840: "Resolved",
    841: "Closed",
    842: "Rejected",
    843: "In progress someone",
    844: "Resolved someone",
    845: "Escalated to L2",
    846: "Accept",
    847: "Accepted someone",
  };
  return statusMap[statusId] || "Open";
};

// Date formatting function (same as in parent component)
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";

  const date = new Date(dateString);
  if (isNaN(date)) return "N/A";

  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear()).toString();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const formattedHours = String(hours).padStart(2, "0");

  const formattedTime = `${formattedHours}:${minutes} ${ampm}`;

  return `${dd}-${mm}-${yyyy} ${formattedTime}`;
};

// Status badge component (same as in parent component)
const renderStatusBadge = (status) => {
  if (!status) return "NA";

  let backgroundColor = "";
  let dotColor = "";
  let textColor = "";

  switch (status) {
    case "Open":
      backgroundColor = "#FDE2E4";
      dotColor = "#D90429";
      textColor = "#D90429";
      break;
    case "In Progress":
    case "In progress someone":
      backgroundColor = "#FFF1E6";
      dotColor = "#FF7F11";
      textColor = "#FF7F11";
      break;
    case "Hold":
      backgroundColor = "#FFF8E1";
      dotColor = "#FFC107";
      textColor = "#FFC107";
      break;
    case "Resolved":
    case "Resolved someone":
      backgroundColor = "#E2F0CB";
      dotColor = "#228B22";
      textColor = "#228B22";
      break;
    case "Closed":
      backgroundColor = "#E0E8F9";
      dotColor = "#304FFE";
      textColor = "#304FFE";
      break;
    case "Rejected":
      backgroundColor = "#FBE3E8";
      dotColor = "#B00020";
      textColor = "#B00020";
      break;
    case "Escalated to L2":
      backgroundColor = "#E3F2FD";
      dotColor = "#4CA2C4";
      textColor = "#4CA2C4";
      break;
    case "Accept":
      backgroundColor = "#E8F5E9";
      dotColor = "#2E7D32";
      textColor = "#2E7D32";
      break;
    default:
      return "NA";
  }

  return (
    <div className="status-badge-container" style={{ position: "relative" }}>
      <div
        className="status-badge"
        style={{
          backgroundColor,
          color: textColor,
          borderRadius: "5px",
          padding: "4px 10px 4px 20px",
          display: "inline-block",
          width: "120px",
          height: "28px",
          lineHeight: "20px",
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: "12px",
          position: "relative",
          textAlign: "left",
        }}
      >
        <div
          className="status-dot"
          style={{
            backgroundColor: dotColor,
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            position: "absolute",
            left: "8px",
            top: "10px",
          }}
        ></div>
        {status}
      </div>
    </div>
  );
};

// Criticality renderer (same as in parent component)
const renderCriticality = (criticality) => {
  if (!criticality) return "NA";

  let textColor = "";

  switch (criticality.toUpperCase()) {
    case "CRITICAL":
      textColor = "#DC3545";
      break;
    case "HIGH":
      textColor = "#FD7E14";
      break;
    case "LOW":
      textColor = "#28A745";
      break;
    case "NORMAL":
      textColor = "#FFC107";
      break;
    default:
      textColor = "#808080";
  }

  return (
    <span
      style={{
        color: textColor,
        fontWeight: "bold",
        fontFamily: "Poppins",
        fontSize: "12px",
      }}
    >
      {criticality}
    </span>
  );
};

// Actions Dropdown Component (same as in parent component)
const ActionsDropdown = ({ grievance, userLevel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if dropdown should be enabled
  const shouldShowAccept =
    grievance.originalData.escalated &&
    userLevel &&
    grievance.originalData?.userLevel &&
    userLevel === grievance.originalData.userLevel;

  const shouldShowReopen =
    userLevel &&
    grievance.originalData?.userLevel &&
    userLevel === grievance.originalData.userLevel &&
    grievance.status === "Closed" && !grievance.originalData.reOpenedTo;

  // Show dropdown if ANY action is available
  const shouldShowDropdown = shouldShowAccept || shouldShowReopen;

  if (!shouldShowDropdown) {
    return null;
  }

  const handleActionClick = (action) => {
    setIsOpen(false);
    if (action === "accept") {
      // Handle accept action
      console.log("Accept grievance:", grievance.id);
    } else if (action === "reopen") {
      // Handle reopen action
      console.log("Reopen grievance:", grievance.id);
    }
  };

  return (
    <div
      className="actions-dropdown-container"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <button
        className="actions-dropdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: "#1a7a73",
          color: "white",
          border: "none",
          borderRadius: "4px",
          padding: "4px 12px",
          fontSize: "11px",
          fontFamily: "Poppins",
          fontWeight: 500,
          cursor: "pointer",
          height: "28px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          minWidth: "80px",
          justifyContent: "space-between",
        }}
        title="Actions"
      >
        Actions
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div
          className="actions-dropdown-menu"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            minWidth: "120px",
            marginTop: "2px",
          }}
        >
          {shouldShowAccept && (
            <button
              onClick={() => handleActionClick("accept")}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                fontSize: "12px",
                fontFamily: "Poppins",
                textAlign: "left",
                borderBottom: "1px solid #f0f0f0",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              Accept
            </button>
          )}
          {shouldShowReopen && (
            <button
              onClick={() => handleActionClick("reopen")}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "none",
                backgroundColor: "transparent",
                cursor: "pointer",
                fontSize: "12px",
                fontFamily: "Poppins",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "transparent")
              }
            >
              Reopen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

//Expandable Table function for grievance details
const ExpandableTablegrievancetable = (props) => {
  const {
    columns,
    data,
    numberOfRows,
    headerTableHeadStyle = {},
    tableStyle = {},
    showRowCount = true,
    rowCount,
    setSearchData = () => {},
    enableColumnFilters = false,
    disbalePaginationFilter = false,
    disbalePaginationFilter1 = false,
    disbalePaginationFilter2 = false,
  } = props;

  // Track expansion state for grievance rows
  const [expandedGrievanceIds, setExpandedGrievanceIds] = useState({});
  const [userLevel, setUserLevel] = useState(null);
  
  const navigate = useNavigate();
  const { t } = useTranslation();


  // Add this function to your expandable table component
const transformGrievanceStatusEntities = (grievanceStatusMapEntities) => {
  if (!grievanceStatusMapEntities || !Array.isArray(grievanceStatusMapEntities)) return [];
  
  return grievanceStatusMapEntities.map(entity => ({
    ...entity, // Keep all original properties
    id: entity.id,
    createdBy: entity.createdBy,
    grievanceId: entity.grievanceId,
    createdOn: entity.createdOn,
    status: entity.status,
    statusText: getStatusFromId(entity.status) // Add readable status text
  }));
};

  // Fetch user level from themeURL
  useEffect(() => {
    const fetchUserLevel = async () => {
      try {
        const response = await getData(getThemeURL);
        if (
          response &&
          Array.isArray(response) &&
          response.length > 0
        ) {
          setUserLevel(response[0].user_level);
        }
      } catch (error) {
        console.error("Error fetching user level:", error);
      }
    };
    fetchUserLevel();
  }, []);

  // Toggle expansion for grievance row to show old tickets
  const toggleGrievanceExpansion = (rowId) => {
    setExpandedGrievanceIds((prev) => {
      const isCurrentlyExpanded = !!prev[rowId];
      return isCurrentlyExpanded ? {} : { [rowId]: true };
    });
  };

  // Transform old grievance data to match parent table structure
const transformOldGrievanceData = (oldGrievance) => {
  // Get the latest status update from grievanceStatusMapEntities for old grievances
  const statusUpdates = oldGrievance.grievanceStatusMapEntities || [];
  let lastStatusUpdated = "N/A";
  
  if (statusUpdates.length > 0) {
    // Sort by createdOn date to get the latest status update
    const sortedStatusUpdates = [...statusUpdates].sort((a, b) => 
      new Date(b.createdOn) - new Date(a.createdOn)
    );
    lastStatusUpdated = formatDateTime(sortedStatusUpdates[0].createdOn);
  }

  return {
    id: oldGrievance.grievanceSeqId,
    bank: oldGrievance.bankName,
    branch: oldGrievance.branchName || "NA",
    bankCode: oldGrievance.bankCode,
    branchCode: null,
    channel: "Web",
    type: oldGrievance.issueTypeName,
    subject: oldGrievance.subject,
    details: oldGrievance.description
      ? oldGrievance.description.replace(/<[^>]*>/g, "")
      : "",
    date: formatDateTime(oldGrievance.ticketDateAndTime || oldGrievance.created_on),
    criticality: oldGrievance.priority,
    status: getStatusFromId(oldGrievance.status),
    handler: oldGrievance.assignedTeamName,
    issueTypeName: oldGrievance.issueTypeName,
    userLevels: oldGrievance.userLevel,
    customerName: oldGrievance.customerName,
    email: oldGrievance.email,
    phoneNumber: oldGrievance.phoneNumber,
    issueTypeSubCatName: oldGrievance.issueTypeSubCatName,
    resolutionMethod: oldGrievance.resoultionName,
    mediaList: oldGrievance.mediaList || [],
    originalData: {
      ...oldGrievance,
      grievanceStatusMapEntities: transformGrievanceStatusEntities(oldGrievance.grievanceStatusMapEntities)
    },
    isDisable: oldGrievance.is_disable,
    isOldGrievance: true,
    reopenedRemarks: oldGrievance.reOpenedRemarks,
    reopenedDate: formatDateTime(oldGrievance.reOpenedDateAndTime),
    closedDate: formatDateTime(oldGrievance.updated_on),
    lastStatusUpdated: lastStatusUpdated, // Add this line for old grievances
  };
};


  // Cell renderers for old grievance table
  const renderOldGrievanceCell = (column, cellValue, rowData) => {
    switch (column.accessor) {
      case "id":
        return (
          <span
            style={{
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {cellValue || "NA"}
          </span>
        );
      
      case "bank":
      case "branch":
      case "date":
      case "handler":
      case "issueTypeName":
      case "userLevels":
      case "lastStatusUpdated":
        return (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {cellValue || "NA"}
          </span>
        );
      
      case "criticality":
        return renderCriticality(cellValue);
      
      case "status":
        return renderStatusBadge(cellValue || "Open");
      
      case "action":
        return (
          <div
            className="usertable__actionsicon"
            style={{ display: "flex", gap: "8px", alignItems: "center" }}
          >
            {/* View Icon */}
            <span
              onClick={() =>
                navigate("/grievance/view-raise-ticket", {
                  state: { data: rowData, isOldGrievance: true },
                })
              }
              style={{ cursor: "pointer" }}
            >
              <Viewicon value={rowData} />
            </span>

            {/* Actions Dropdown - Only show if conditions are met */}
            <ActionsDropdown
              grievance={rowData}
              userLevel={userLevel}
            />
          </div>
        );
      
      default:
        return (
          <span
            style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
          >
            {cellValue || "NA"}
          </span>
        );
    }
  };

  // Create the same columns structure as parent table for old grievances
  const getOldGrievanceColumns = () => {
    return [
      {
        Header: t("Ticket ID"),
        accessor: "id",
        disableSortBy: true,
      },
      {
        Header: t("Bank Name"),
        accessor: "bank",
        disableSortBy: true,
      },
      {
        Header: t("Branch Name"),
        accessor: "branch",
        disableSortBy: true,
      },
      {
        Header: t("Issue Reported Date & Time"),
        accessor: "date",
        disableSortBy: true,
      },
      {
        Header: t("Assigned Department"),
        accessor: "handler",
        width: 100,
        disableSortBy: true,
      },
      // {
      //   Header: t("Category"),
      //   accessor: "issueTypeName",
      //   disableSortBy: true,
      // },
      {
        Header: t("Escalation  Level"),
        accessor: "userLevels",
        width: 80,
        disableSortBy: true,
      },
      {
        Header: t("Priority"),
        accessor: "criticality",
        disableSortBy: true,
      },
      {
        Header: t("Status"),
        accessor: "status",
        disableSortBy: false,
      },
      {
  Header: "Status Updated Date & Time",
  accessor: "lastStatusUpdated",
  disableSortBy: true,
  Cell: ({ value }) => (
    <span
      style={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: 500 }}
    >
      {value || "N/A"}
    </span>
  ),
},
      {
        Header: t("Action"),
        accessor: "action",
        disableSortBy: true,
      },
    ];
  };

  // Render old grievances for a parent grievance
  const renderOldGrievancesRow = (row) => {
    const oldGrievances = row.original.originalData?.oldGrievanceInformationEntity || [];
    const oldGrievanceColumns = getOldGrievanceColumns();
    const transformedOldGrievances = oldGrievances.map(transformOldGrievanceData);

    return (
      <tr>
        <td colSpan={columns.length + 1}>
          <div
            className="old-grievances-container"
            style={{
              marginLeft: "20px",
              marginTop: "15px",
              marginBottom: "15px",
              width: "98%",
              backgroundColor: "#f8f9fa",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #e9ecef",
            }}
          >
            <h6
              style={{
                fontWeight: "600",
                color: "#1a7a73",
                marginBottom: "15px",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* <span style={{ 
                backgroundColor: "#1a7a73", 
                color: "white", 
                borderRadius: "4px",
                padding: "2px 8px",
                fontSize: "12px",
              }}>
                History
              </span> */}
              Ticket Lineage ({oldGrievances.length} Record{oldGrievances.length !== 1 ? 's' : ''})
            </h6>

            {oldGrievances.length > 0 ? (
              <div className="old-grievances-table-container">
                <table
                  className="table"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <thead style={{ backgroundColor: "#1a7a73" }}>
                    <tr>
                      {oldGrievanceColumns.map((column, index) => (
                        <th
                          key={column.accessor || index}
                          style={{
                            padding: "10px 8px",
                            textAlign: "left",
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "600",
                            fontFamily: "Poppins",
                            borderBottom: "2px solid #3a8ba5",
                          }}
                        >
                          {column.Header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transformedOldGrievances.map((oldGrievance, index) => (
                      <tr 
                        key={oldGrievance.id} 
                        style={{ 
                          backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#e3f2fd";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#f8f9fa";
                        }}
                      >
                        {oldGrievanceColumns.map((column, colIndex) => (
                          <td
                            key={colIndex}
                            style={{
                              padding: "10px 8px",
                              textAlign: "left",
                              fontSize: "12px",
                              fontFamily: "Poppins",
                              fontWeight: 500,
                              borderBottom: "1px solid #f1f1f1",
                            }}
                          >
                            {renderOldGrievanceCell(column, oldGrievance[column.accessor], oldGrievance)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                textAlign: "center", 
                padding: "30px",
                color: "#6c757d",
                fontStyle: "italic",
                backgroundColor: "white",
                borderRadius: "6px",
              }}>
                No previous ticket history available
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    rows,
    state: { pageIndex, globalFilter, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: { pageIndex: 0, pageSize: numberOfRows },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const translateNumber = (number) => {
    const numberString = number.toString();
    return i18n.t(`numbers.${numberString}`, { defaultValue: numberString });
  };

  let tempRowCount = globalFilter ? rows.length : rowCount || data.length;
  let tempPageIndex = pageIndex;
  let tempPageSize = pageSize;

  if (tempPageIndex === undefined || tempPageIndex === null) {
    tempPageIndex = 0;
  }
  if (tempRowCount === undefined || tempRowCount === null) {
    tempRowCount = 0;
  }
  if (tempPageSize === undefined || tempPageSize === null) {
    tempPageSize = 10;
  }

  const startIndex = tempPageIndex * tempPageSize + 1 || 1;
  const endIndex =
    Math.min((tempPageIndex + 1) * tempPageSize, tempRowCount) || tempRowCount;

  const translatedStartIndex = startIndex;
  const translatedEndIndex = endIndex;
  const translatedRowCount = tempRowCount;

  useEffect(() => {
    setPageSize(numberOfRows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfRows]);

  useEffect(() => {
    if (globalFilter) {
      const pageData = page?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    } else {
      const pageData = rows?.map((row) => row.original);
      setSearchData(pageData ? pageData : []);
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, rows]);

  return (
    <div className="tabl_container">
      <TableHeader
        {...props}
        searchValue={globalFilter || ""}
        onSearchHandler={setGlobalFilter}
      />
      <div className="iccc__table--container" style={tableStyle}>
        <table {...getTableProps()} className="table iccc--table">
          <thead style={{ backgroundColor: "#1a7a73" }}>
            <tr>
              <th
                style={{
                  width: "30px",
                  textAlign: "left",
                  color: "white",
                  backgroundColor: "#1a7a73",
                }}
              >
                {t("")}
              </th>
              {headerGroups.map((headerGroup, index) => (
                <React.Fragment key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      style={headerTableHeadStyle}
                      key={index}
                    >
                      {column.render("Header")}
                      {!column.disableSortBy &&
                        (column.isSorted ? (
                          column.isSortedDesc ? (
                            <SvgSortdownarrow />
                          ) : (
                            <SvgSortuparrow />
                          )
                        ) : (
                          <SvgSorting />
                        ))}
                      {enableColumnFilters && column.canFilter && (
                        <div>{column.render("Filter")}</div>
                      )}
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map((row, i) => {
                prepareRow(row);
                const isExpanded = expandedGrievanceIds[row.id];
                const hasOldGrievances = row.original.originalData?.oldGrievanceInformationEntity?.length > 0;

                return (
                  <React.Fragment key={row.id}>
                    <tr {...row.getRowProps()}>
                      <td style={{ textAlign: "left" }}>
                        {hasOldGrievances && (
                          <span
                            onClick={() => toggleGrievanceExpansion(row.id)}
                            style={{ 
                              cursor: "pointer", 
                              fontSize: "16px",
                              // color: "#1a7a73",
                              fontWeight: "bold",
                              display: "inline-block",
                              width: "20px",
                              height: "20px",
                              textAlign: "center",
                              lineHeight: "18px",
                              border: "1px solid #1a7a73",
                              borderRadius: "3px",
                              backgroundColor: isExpanded ? "#1a7a73" : "white",
                              color: isExpanded ? "white" : "#1a7a73",
                            }}
                            title={isExpanded ? "Collapse history" : "Expand history"}
                          >
                            {isExpanded ? "−" : "+"}
                          </span>
                        )}
                        {!hasOldGrievances && (
                          <span style={{ 
                            color: "#ccc",
                            fontSize: "16px",
                            display: "inline-block",
                            width: "20px",
                            textAlign: "center",
                          }}>
                            •
                          </span>
                        )}
                      </td>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          key={cell.column.id}
                          style={{ textAlign: "left" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                    {isExpanded && hasOldGrievances && renderOldGrievancesRow(row)}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-data-text">
                  No data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        {!disbalePaginationFilter2 && (
          <div className="float-start m-2 mt-2 ms-3">
            {showRowCount && (
              <div className="float-start m-2 mt-1 ms-3 pagecount-style">
                {t("showingentriestable.showing")} {translatedStartIndex}{" "}
                {t("showingentriestable.to")} {translatedEndIndex}{" "}
                {t("showingentriestable.of")} {translatedRowCount}{" "}
                {t("showingentriestable.entries")}
              </div>
            )}
          </div>
        )}
        <div className="pagination">
          {!disbalePaginationFilter && (
            <div className="d-flex">
              <span className="pagination_gotopage">
                Go to page:
                <input
                  type="number"
                  min="1"
                  defaultValue={pageIndex + 1}
                  onChange={(e) => {
                    let pageNumber = Number(e.target.value);
                    if (pageNumber < 1 || isNaN(pageNumber)) {
                      pageNumber = 1; // Ensures minimum value of 1
                    }
                    gotoPage(pageNumber - 1);
                  }}
                  className="pagination_gotopage-input"
                />
              </span>
              <span>
                <select
                  className="pagination_select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  {[10, 50, 100, 500, 1000, 2000].map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      show {pageSize}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          )}
          {!disbalePaginationFilter1 && (
            <>
              <button
                className="pagebtn mx-2"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                {"<"}
              </button>
              <button className="pagination_page">
                {translateNumber(pageIndex + 1)}{" "}
              </button>
              <button
                className="pagebtn"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                {">"}
              </button>{" "}
              <span className="right_pageno">
                {t("pagination.page_of", {
                  page: translateNumber(pageIndex + 1),
                  total: translateNumber(pageOptions.length),
                })}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandableTablegrievancetable;